import { execFile } from 'node:child_process';
import { randomBytes } from 'node:crypto';
import { existsSync } from 'node:fs';
import { promises as fs } from 'node:fs';
import { homedir } from 'node:os';
import path from 'node:path';
import { promisify } from 'node:util';
import type {
    VaultCipherEnvelope,
    VaultKeyring,
    VaultLocalDevice,
    VaultUnlockResult,
} from '../types/vault.types.js';
import { VaultCryptoService } from './vault-crypto.service.js';

const execFileAsync = promisify(execFile);
const SERVICE_NAME = 'jai1-vault';

export interface VaultCredentialStore {
    isSupported(): boolean;
    get(account: string): Promise<string | null>;
    set(account: string, secret: string): Promise<void>;
    delete(account: string): Promise<void>;
}

export interface VaultTrustedDeviceRecord {
    schemaVersion: 1;
    vaultId: string;
    deviceId: string;
    deviceName: string;
    keychainAccount: string;
    encryptedVmk: VaultCipherEnvelope;
    createdAt: string;
    updatedAt: string;
}

export interface VaultTrustedDeviceStatus {
    supported: boolean;
    trusted: boolean;
    deviceId?: string;
    deviceName?: string;
}

export class MacosKeychainCredentialStore implements VaultCredentialStore {
    constructor(private readonly platform: NodeJS.Platform = process.platform) {}

    isSupported(): boolean {
        return this.platform === 'darwin';
    }

    async get(account: string): Promise<string | null> {
        if (!this.isSupported()) return null;
        try {
            const { stdout } = await execFileAsync('security', [
                'find-generic-password',
                '-a',
                account,
                '-s',
                SERVICE_NAME,
                '-w',
            ]);
            return stdout.trim() || null;
        } catch {
            return null;
        }
    }

    async set(account: string, secret: string): Promise<void> {
        if (!this.isSupported()) {
            throw new Error('Trusted device unlock is currently supported on macOS Keychain only.');
        }
        await execFileAsync('security', [
            'add-generic-password',
            '-a',
            account,
            '-s',
            SERVICE_NAME,
            '-w',
            secret,
            '-U',
        ]);
    }

    async delete(account: string): Promise<void> {
        if (!this.isSupported()) return;
        try {
            await execFileAsync('security', [
                'delete-generic-password',
                '-a',
                account,
                '-s',
                SERVICE_NAME,
            ]);
        } catch {
            // Already removed.
        }
    }
}

export class MemoryVaultCredentialStore implements VaultCredentialStore {
    private readonly secrets = new Map<string, string>();

    isSupported(): boolean {
        return true;
    }

    async get(account: string): Promise<string | null> {
        return this.secrets.get(account) ?? null;
    }

    async set(account: string, secret: string): Promise<void> {
        this.secrets.set(account, secret);
    }

    async delete(account: string): Promise<void> {
        this.secrets.delete(account);
    }
}

export class VaultTrustedDeviceService {
    private readonly crypto = new VaultCryptoService();
    private readonly trustPath: string;

    constructor(
        private readonly rootDir: string = path.join(homedir(), '.jai1', 'vault'),
        private readonly credentials: VaultCredentialStore = new MacosKeychainCredentialStore()
    ) {
        this.trustPath = path.join(rootDir, 'trusted-device.json');
    }

    isSupported(): boolean {
        return this.credentials.isSupported();
    }

    async trust(vaultId: string, device: VaultLocalDevice, vmk: Buffer): Promise<VaultTrustedDeviceRecord> {
        if (!this.credentials.isSupported()) {
            throw new Error('Trusted device unlock is currently supported on macOS Keychain only.');
        }
        const secret = randomBytes(32).toString('base64');
        const account = this.keychainAccount(vaultId, device.deviceId);
        await this.credentials.set(account, secret);

        const now = new Date().toISOString();
        const record: VaultTrustedDeviceRecord = {
            schemaVersion: 1,
            vaultId,
            deviceId: device.deviceId,
            deviceName: device.deviceName,
            keychainAccount: account,
            encryptedVmk: this.crypto.encryptBuffer(
                Buffer.from(secret, 'base64'),
                vmk,
                this.aad(vaultId, device.deviceId)
            ),
            createdAt: now,
            updatedAt: now,
        };
        await fs.mkdir(this.rootDir, { recursive: true, mode: 0o700 });
        await fs.writeFile(this.trustPath, JSON.stringify(record, null, 2), { mode: 0o600 });
        return record;
    }

    async unlock(keyring: VaultKeyring, device: VaultLocalDevice | null): Promise<VaultUnlockResult | null> {
        if (!device || !this.credentials.isSupported()) return null;
        const record = await this.loadRecord();
        if (!record || record.vaultId !== keyring.vaultId || record.deviceId !== device.deviceId) return null;
        const secret = await this.credentials.get(record.keychainAccount);
        if (!secret) return null;

        try {
            const vmk = this.crypto.decryptBuffer(
                Buffer.from(secret, 'base64'),
                record.encryptedVmk,
                this.aad(record.vaultId, record.deviceId)
            );
            return { vaultId: keyring.vaultId, vmk };
        } catch {
            return null;
        }
    }

    async untrust(keyring: VaultKeyring, device: VaultLocalDevice | null): Promise<void> {
        const record = await this.loadRecord();
        const account = record?.keychainAccount || (device ? this.keychainAccount(keyring.vaultId, device.deviceId) : null);
        if (account) await this.credentials.delete(account);
        if (existsSync(this.trustPath)) await fs.rm(this.trustPath, { force: true });
    }

    async status(keyring: VaultKeyring, device: VaultLocalDevice | null): Promise<VaultTrustedDeviceStatus> {
        const supported = this.credentials.isSupported();
        if (!device) return { supported, trusted: false };
        const record = await this.loadRecord();
        const trusted = !!record && record.vaultId === keyring.vaultId && record.deviceId === device.deviceId
            && !!(await this.credentials.get(record.keychainAccount));
        return {
            supported,
            trusted,
            deviceId: device.deviceId,
            deviceName: device.deviceName,
        };
    }

    private async loadRecord(): Promise<VaultTrustedDeviceRecord | null> {
        if (!existsSync(this.trustPath)) return null;
        const raw = await fs.readFile(this.trustPath, 'utf-8');
        return JSON.parse(raw) as VaultTrustedDeviceRecord;
    }

    private keychainAccount(vaultId: string, deviceId: string): string {
        return `vault:${vaultId}:device:${deviceId}`;
    }

    private aad(vaultId: string, deviceId: string): string {
        return `jai1-vault-trusted-device:${vaultId}:${deviceId}`;
    }
}
